import {
  isKnowledgeAudioFile,
  isKnowledgeMediaFile,
  isKnowledgeVideoUrl,
} from '../KnowledgeHubPage/videoUrl'
import { isHtmlDocument } from './HtmlDocumentPreview'
import { isSlideDocument } from './SlideDocumentPreview'
import { describeTabularDocument, TabularFormat } from './TabularDocumentPreview'
import { isEmbeddableHttpUrl } from './WebsitePreview'

export interface DocumentPreviewSource {
  mimeType?: string | null
  originalFilename?: string | null
  sourceUrl?: string | null
}

export interface DocumentPreviewKind {
  /** Trimmed source URL, or null when this is not a link document. */
  sourceUrl: string | null
  isLinkDocument: boolean
  isUploadedMedia: boolean
  isAudioDocument: boolean
  isVideoDocument: boolean
  isHtmlUpload: boolean
  isSlideUpload: boolean
  isFullBleedPreview: boolean
  tabularFormat: TabularFormat | null
  /** Body needs a flex frame so the renderer can size itself. */
  usesFlexPreviewBody: boolean
  /** Paged documents keep the numbered page rail and the prev/next pager. */
  isPagedDocument: boolean
}

/**
 * Single source of truth for "what kind of preview does this document get".
 * Both the page shell (which decides whether to prefetch page spans) and the
 * preview pane (which picks a renderer) read from here, so the two can never
 * disagree about a document.
 */
export function describePreviewKind(
  source: DocumentPreviewSource,
): DocumentPreviewKind {
  const sourceUrl = source.sourceUrl?.trim() || null
  const isLinkDocument = Boolean(sourceUrl && isEmbeddableHttpUrl(sourceUrl))
  const isUploadedMedia = isKnowledgeMediaFile(
    source.mimeType,
    source.originalFilename,
  )
  const isAudioDocument = isKnowledgeAudioFile(
    source.mimeType,
    source.originalFilename,
  )
  const isVideoDocument =
    Boolean(sourceUrl && isKnowledgeVideoUrl(sourceUrl)) || isUploadedMedia
  const isHtmlUpload =
    !isLinkDocument && isHtmlDocument(source.mimeType, source.originalFilename)
  const isFullBleedPreview = isLinkDocument || isHtmlUpload || isUploadedMedia
  // A deck keeps the thumbnail rail — it has real slide boundaries — but its
  // body needs the flex layout so the fixed-aspect slide frame can size itself.
  const isSlideUpload =
    !isLinkDocument &&
    !isHtmlUpload &&
    isSlideDocument(source.mimeType, source.originalFilename)
  // A CSV or spreadsheet gets the flex body too: the grid scrolls inside its own
  // frame instead of pushing the page into one very tall scroll region.
  const tabularFormat =
    isLinkDocument || isHtmlUpload || isSlideUpload
      ? null
      : describeTabularDocument(source.mimeType, source.originalFilename)
  const usesFlexPreviewBody =
    isFullBleedPreview || isSlideUpload || tabularFormat != null

  return {
    sourceUrl,
    isLinkDocument,
    isUploadedMedia,
    isAudioDocument,
    isVideoDocument,
    isHtmlUpload,
    isSlideUpload,
    isFullBleedPreview,
    tabularFormat,
    usesFlexPreviewBody,
    isPagedDocument: !isFullBleedPreview,
  }
}
